import React, { useEffect, useRef, useState } from 'react';

import { blobToDataURL } from '@repo/editor/src/utils';
import { withEventScope } from '@repo/event-processor';

import ImageEditor from 'components/AiTools/ImageEditor/ImageEditor';
import SimpleEditor from 'components/BackgroundReplacer/SourceTemplate/SimpleEditor/SimpleEditor';
import { getImageDimensions } from 'global/utils/fileUtils';
import { useBackgroundReplacerStore } from 'stores/backgroundReplacerStore/backgroundReplacerStore';

import { Image as ImageComponents } from '../styles';

import { ImageWrapper } from './styles';
import { SourceTemplateProps } from './types';

const getImageAsBase64 = async (url: string): Promise<string | null> => {
  const image = await fetch(url);
  if (!image) {
    return null;
  }

  const blob = await image.blob();
  const result = await blobToDataURL(blob);
  return result && typeof result === 'string' ? result : null;
};

const SourceTemplate: React.FC<SourceTemplateProps> = ({
  isLoading,
  initialImageSrc,
  productImageSrc,
  onReplace,
  background,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<SVGImageElement>(null);
  const { registerGetEditedImageHandler } = useBackgroundReplacerStore();

  const [imageSize, setImageSize] = useState<{
    width: number;
    height: number;
  } | null>(null);

  let wrapperWidth = 0;
  let wrapperHeight = 0;
  if (wrapperRef.current) {
    const { width, height } = wrapperRef.current.getBoundingClientRect();
    wrapperWidth = width;
    wrapperHeight = height;
  }

  useEffect(() => {
    if (productImageSrc) {
      getImageDimensions(productImageSrc).then(({ width, height }) => {
        const ratio = width / height;
        if (width > height) {
          width = wrapperWidth * 0.6;
          height = width / ratio;
        } else {
          height = wrapperHeight * 0.6;
          width = height * ratio;
        }
        setImageSize({ width, height });
      });
    }
  }, [productImageSrc, wrapperHeight, wrapperWidth]);

  useEffect(() => {
    registerGetEditedImageHandler(async (): Promise<File | null> => {
      const svgElement = document.createElement('svg');
      // setAttribute always lower-case the name, but it's incorrect for svg
      svgElement.setAttributeNS(
        null,
        'viewBox',
        `0 0 ${wrapperWidth} ${wrapperHeight}`
      );
      svgElement.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      const imageElement = document.createElement('image');
      imageRef.current?.getAttributeNames().forEach((name: string) => {
        const value = imageRef.current?.getAttribute(name);
        if (value) {
          imageElement.setAttribute(name, value);
        }
      });
      const url = imageElement.getAttribute('href');
      if (!url) {
        return null;
      }
      const base64Image = await getImageAsBase64(url);
      if (!base64Image) {
        return null;
      }
      imageElement.setAttribute('href', base64Image);
      svgElement.appendChild(imageElement);
      const svgtext = svgElement.outerHTML;
      return new File([svgtext], 'file.svg', { type: 'image/svg+xml' });
    });
  }, [wrapperWidth, wrapperHeight, registerGetEditedImageHandler]);

  return (
    <ImageEditor
      background={background}
      isLoading={isLoading}
      onReplace={onReplace}
    >
      <ImageWrapper ref={wrapperRef}>
        {productImageSrc && imageSize ? (
          <SimpleEditor
            ref={imageRef}
            height={wrapperHeight}
            image={productImageSrc!}
            imageHeight={imageSize.height}
            imageWidth={imageSize.width}
            width={wrapperWidth}
          />
        ) : (
          <ImageComponents
            alt="Image to replace background of"
            src={initialImageSrc}
          />
        )}
      </ImageWrapper>
    </ImageEditor>
  );
};

export default withEventScope(SourceTemplate, {
  module: 'sourceTemplateEditor',
});
